import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, Gift } from "lucide-react";

const TASKS = [
  {
    id: "classes",
    label: "Recruit one gladiator of each class",
    check: (d) => d.all_classes.every((c) => d.classes_recruited.includes(c)),
    detail: (d) => {
      const missing = d.all_classes.filter((c) => !d.classes_recruited.includes(c));
      return missing.length ? `Missing: ${missing.join(", ")}` : "All classes recruited";
    },
    link: "/recruit",
  },
  {
    id: "forge_weapons",
    label: "Forge 3 weapons",
    check: (d) => d.weapons_owned >= 3,
    detail: (d) => `${Math.min(d.weapons_owned, 3)}/3`,
    link: "/equipment",
  },
  {
    id: "forge_armor",
    label: "Forge 3 armor",
    check: (d) => d.armor_owned >= 3,
    detail: (d) => `${Math.min(d.armor_owned, 3)}/3`,
    link: "/equipment",
  },
  {
    id: "equip_weapons",
    label: "Equip 3 weapons",
    check: (d) => d.weapons_equipped >= 3,
    detail: (d) => `${Math.min(d.weapons_equipped, 3)}/3`,
    link: "/equipment?tab=armory",
  },
  {
    id: "equip_armor",
    label: "Equip 3 armor",
    check: (d) => d.armor_equipped >= 3,
    detail: (d) => `${Math.min(d.armor_equipped, 3)}/3`,
    link: "/equipment?tab=armory",
  },
  {
    id: "alliance",
    label: "Join or create an alliance",
    check: (d) => d.has_alliance,
    detail: (d) => d.has_alliance ? "Done" : "Not yet",
    link: "/alliance",
  },
  {
    id: "tournaments",
    label: "Enter 2 tournaments",
    check: (d) => d.tournament_entries >= 2,
    detail: (d) => `${Math.min(d.tournament_entries, 2)}/2`,
    link: "/events",
  },
  {
    id: "repair",
    label: "Repair a piece of equipment",
    check: (d) => d.has_repaired,
    detail: (d) => d.has_repaired ? "Done" : "Not yet",
    link: "/equipment?tab=armory",
  },
  {
    id: "arena",
    label: "Complete 10 arena battles",
    check: (d) => d.arena_battles >= 10,
    detail: (d) => `${Math.min(d.arena_battles, 10)}/10`,
    link: "/challenges",
  },
  {
    id: "quests",
    label: "Start a quest",
    check: (d) => d.quests_started >= 1,
    detail: (d) => d.quests_started >= 1 ? "Done" : "Not yet",
    link: "/events?tab=quests",
  },
  {
    id: "sands",
    label: "Visit the Sands",
    check: (d) => d.sands_completed >= 1,
    detail: (d) => d.sands_completed >= 1 ? "Done" : "Not yet",
    link: "/events?tab=adventure",
  },
];

export default function NewPlayerChecklist() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("checklist_collapsed") === "true");
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("checklist_dismissed") === "true");
  const [claiming, setClaiming] = useState(false);
  const [justClaimed, setJustClaimed] = useState(false);
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    if (!user || dismissed) return;
    api.get("/checklist")
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [user, dismissed]);

  // Countdown timer synced to server clock
  useEffect(() => {
    if (!data || data.expired || !data.expires_at || !data.server_now) return;
    // Calculate offset between local clock and server clock
    const serverNow = new Date(data.server_now).getTime();
    const localNow = Date.now();
    const offset = serverNow - localNow;

    const tick = () => {
      const serverTime = Date.now() + offset;
      const expiresAt = new Date(data.expires_at).getTime();
      const remaining = expiresAt - serverTime;
      if (remaining <= 0) {
        setCountdown("Expired");
        return;
      }
      const d = Math.floor(remaining / 86400000);
      const h = Math.floor((remaining % 86400000) / 3600000);
      const m = Math.floor((remaining % 3600000) / 60000);
      if (d > 0) setCountdown(`${d}d ${h}h remaining`);
      else if (h > 0) setCountdown(`${h}h ${m}m remaining`);
      else setCountdown(`${m}m remaining`);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [data]);

  if (!data || dismissed || data.expired) return null;

  const completed = TASKS.filter((t) => t.check(data)).length;
  const total = TASKS.length;
  const allDone = completed === total;

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("checklist_collapsed", String(next));
  };

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem("checklist_dismissed", "true");
  };

  const claimReward = async () => {
    setClaiming(true);
    try {
      await api.post("/checklist/claim");
      setJustClaimed(true);
      setData((d) => ({ ...d, reward_claimed: true }));
    } catch {
      // already claimed or not ready
    } finally {
      setClaiming(false);
    }
  };

  const pct = Math.round((completed / total) * 100);

  return (
    <div data-testid="new-player-checklist" className="border border-amber-900/30 bg-zinc-950/50 mb-8">
      {/* Header */}
      <button
        data-testid="checklist-toggle"
        onClick={toggleCollapse}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-900/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h3 className="font-cinzel text-sm uppercase tracking-wide text-amber-300">
            Lanista&apos;s Path
          </h3>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            {completed}/{total}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Countdown */}
          {countdown && countdown !== "Expired" && (
            <span className="text-[10px] uppercase tracking-widest text-rose-400/80">{countdown}</span>
          )}
          {/* Progress bar */}
          <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[10px] text-zinc-500">{pct}%</span>
          {collapsed ? <ChevronDown size={14} className="text-zinc-500" /> : <ChevronUp size={14} className="text-zinc-500" />}
        </div>
      </button>

      {/* Task list */}
      {!collapsed && (
        <div className="px-4 pb-3 space-y-0.5">
          {TASKS.map((task) => {
            const done = task.check(data);
            return (
              <a
                key={task.id}
                href={task.link}
                data-testid={`checklist-${task.id}`}
                className={`flex items-center gap-3 px-2 py-1.5 rounded-sm transition-colors group ${
                  done
                    ? "opacity-60"
                    : "hover:bg-zinc-900/40"
                }`}
              >
                {done ? (
                  <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
                ) : (
                  <Circle size={15} className="text-zinc-600 group-hover:text-zinc-400 shrink-0" />
                )}
                <span className={`text-sm flex-1 ${done ? "text-zinc-500 line-through decoration-zinc-700" : "text-zinc-300"}`}>
                  {task.label}
                </span>
                <span className={`text-[10px] tabular-nums ${done ? "text-emerald-600" : "text-zinc-600"}`}>
                  {task.detail(data)}
                </span>
              </a>
            );
          })}
          {/* Reward Section */}
          <div className={`mt-2 border rounded-sm px-3 py-2.5 ${
            allDone && !data.reward_claimed
              ? "border-amber-600/60 bg-amber-950/30"
              : "border-zinc-800/60 bg-zinc-900/30"
          }`}>
            <div className="flex items-center gap-2 mb-1.5">
              <Gift size={14} className={allDone && !data.reward_claimed ? "text-amber-400" : "text-zinc-600"} />
              <span className={`text-[11px] uppercase tracking-widest font-semibold ${
                allDone && !data.reward_claimed ? "text-amber-300" : "text-zinc-500"
              }`}>
                Completion Reward
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex gap-3 flex-1">
                {(data.rewards || []).map((r) => (
                  <div key={r.id} className="flex items-center gap-1.5">
                    <img
                      src={r.id.includes("weapon") ? "/rare-chest.png" : "/rare-chest.png"}
                      alt={r.name}
                      className={`w-8 h-8 object-contain ${!allDone && !data.reward_claimed ? "opacity-40 grayscale" : ""}`}
                    />
                    <div>
                      <div className={`text-xs font-medium ${allDone ? "text-zinc-200" : "text-zinc-500"}`}>{r.name}</div>
                      <div className={`text-[10px] ${allDone ? "text-amber-400" : "text-zinc-600"}`}>x{r.qty}</div>
                    </div>
                  </div>
                ))}
              </div>
              {allDone && !data.reward_claimed && !justClaimed && (
                <button
                  data-testid="checklist-claim-reward"
                  onClick={claimReward}
                  disabled={claiming}
                  className="px-4 py-1.5 text-xs font-cinzel uppercase tracking-widest bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50 transition-colors rounded-sm"
                >
                  {claiming ? "Claiming..." : "Claim"}
                </button>
              )}
              {(data.reward_claimed || justClaimed) && (
                <span className="text-[10px] uppercase tracking-widest text-emerald-500 flex items-center gap-1">
                  <CheckCircle2 size={13} /> Claimed
                </span>
              )}
              {!allDone && !data.reward_claimed && (
                <span className="text-[10px] text-zinc-600">{total - completed} tasks remaining</span>
              )}
            </div>
          </div>
          {/* Dismiss */}
          <div className="pt-3 flex justify-end">
            <button
              data-testid="checklist-dismiss"
              onClick={dismiss}
              className="px-4 py-1.5 text-[10px] uppercase tracking-widest font-semibold border rounded-sm transition-all duration-300 border-amber-600/60 text-amber-300 bg-amber-900/20 hover:bg-amber-800/40 hover:border-amber-500 hover:text-amber-200 shadow-[0_0_12px_rgba(217,119,6,0.15)] hover:shadow-[0_0_20px_rgba(217,119,6,0.3)]"
            >
              {allDone && data.reward_claimed ? "Dismiss — Well done, Lanista!" : "Hide checklist"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
